import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type FormHTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from "react";

import { ApiError } from "@/lib/http";

import { cx } from "./cx";

/**
 * The portal's one form validation standard (PHP-3348).
 *
 * `<Form>` renders a `<form noValidate>` and, on submit:
 * - runs the browser's constraint validation over its controls, so `required`, `type="email"`,
 *   `pattern`, `minLength`/`maxLength` and `min`/`max` keep working, plus the optional `validate` rules;
 * - when anything is invalid, blocks the submit, shows an error summary (announced, with a link to each
 *   field), puts each message on its field and moves focus to the first invalid field.
 *
 * Server validation errors (`ApiError.fieldErrors`, passed as `error`) land on the `Field` whose `name`
 * matches (`field` or a dotted path ending in `.field`), are listed in the summary and focus the first
 * one. A field's error clears as soon as the person edits it. An `ErrorAlert` for the same error inside
 * the form stays quiet while the summary covers it, so one error is never shown twice.
 */

/** One problem in the summary. `id` is the control's id; without it the summary shows plain text. */
interface Problem {
  id?: string;
  message: string;
}

interface Registered {
  name?: string;
  label: string;
}

interface FormContextValue {
  /** The message to show on the field with this control id / name, if any. */
  errorFor: (id: string, name?: string) => string | undefined;
  register: (id: string, field: Registered) => () => void;
  /** True when the summary already shows this error, so an `ErrorAlert` should not repeat it. */
  covers: (error: unknown) => boolean;
}

const FormContext = createContext<FormContextValue | null>(null);

/** The surrounding `<Form>`'s state, or null outside one. */
export function useFormContext(): FormContextValue | null {
  return useContext(FormContext);
}

/** A server field error matches a control `name` exactly or as the last part of a dotted path. */
function matchesName(field: string, name: string): boolean {
  return field === name || field.endsWith(`.${name}`);
}

type Control = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

function isControl(el: Element): el is Control {
  return (
    el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement
  );
}

/** The visible label of a control without a registered Field (marker text and icons left out). */
function domLabel(el: Control): string {
  const label = el.labels?.[0];
  if (label) {
    const copy = label.cloneNode(true) as HTMLElement;
    copy.querySelectorAll("[aria-hidden='true'], [data-field-marker]").forEach((n) => n.remove());
    const text = copy.textContent?.trim();
    if (text) return text;
  }
  const labelledBy = el.getAttribute("aria-labelledby");
  if (labelledBy) {
    const text = labelledBy
      .split(/\s+/)
      .map((id) => document.getElementById(id)?.textContent?.trim() ?? "")
      .join(" ")
      .trim();
    if (text) return text;
  }
  return el.getAttribute("aria-label") ?? el.getAttribute("placeholder") ?? el.name ?? "This field";
}

/** "E-mail" → "e-mail", but "API key" stays "API key". */
function lower(label: string): string {
  return /^[A-Z][A-Z]/.test(label) ? label : label.charAt(0).toLowerCase() + label.slice(1);
}

/** A plain-language message for a control the browser says is invalid. */
export function validityMessage(el: Control, label: string): string {
  const v = el.validity;
  const type = el instanceof HTMLInputElement ? el.type : el instanceof HTMLSelectElement ? "select" : "text";
  if (v.valueMissing) {
    if (type === "checkbox") return `${label} must be ticked`;
    if (type === "select" || type === "radio") return `Choose ${lower(label)}`;
    if (type === "file") return `Choose a file for ${lower(label)}`;
    return `${label} is required`;
  }
  if (v.typeMismatch) {
    if (type === "email") return "Enter a valid e-mail address, like name@example.com";
    if (type === "url") return "Enter a valid web address, like https://example.com";
    return `${label} isn't valid`;
  }
  if (v.badInput) return `${label} must be a number`;
  // a pattern's `title` says what the format is, e.g. "Enter the 6-digit code"
  if (v.patternMismatch) return el.title || `${label} isn't in the right format`;
  if (v.tooShort) return `${label} must be at least ${(el as HTMLInputElement).minLength} characters`;
  if (v.tooLong) return `${label} must be ${(el as HTMLInputElement).maxLength} characters or fewer`;
  if (v.rangeUnderflow) return `${label} must be ${(el as HTMLInputElement).min} or more`;
  if (v.rangeOverflow) return `${label} must be ${(el as HTMLInputElement).max} or less`;
  if (v.stepMismatch) return `${label} must be a whole number`;
  return el.validationMessage || `${label} isn't valid`;
}

let generatedIds = 0;

const NOT_TEXT = new Set(["checkbox", "radio", "file", "select-one", "select-multiple", "range", "color"]);

/** Focuses the control with this id and scrolls it into view; used to move focus to the first invalid field. */
export function focusControl(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.focus();
  el.scrollIntoView?.({ block: "center" });
}

export type FormProps = Omit<FormHTMLAttributes<HTMLFormElement>, "onSubmit" | "noValidate"> & {
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  /** The last submit's error (e.g. `mutation.error`); its `fieldErrors` are shown on the matching fields. */
  error?: unknown;
  /**
   * Extra client rules the browser can't express (e.g. "passwords don't match"), keyed by control `name`.
   * Return a message to block the submit; checked after the browser's own rules.
   */
  validate?: (form: HTMLFormElement) => Record<string, string | false | null | undefined> | void;
  /** Extra classes for the error summary (e.g. `w-full` in a wrapping row). */
  summaryClassName?: string;
  children: ReactNode;
};

export function Form({
  onSubmit,
  error,
  validate,
  summaryClassName,
  children,
  className,
  onInput,
  ...rest
}: FormProps) {
  // The Fields inside, by control id (they register themselves).
  const [registry, setRegistry] = useState<Record<string, Registered>>({});
  const register = useCallback((id: string, field: Registered) => {
    setRegistry((prev) =>
      prev[id]?.name === field.name && prev[id]?.label === field.label ? prev : { ...prev, [id]: field },
    );
    return () =>
      setRegistry((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
  }, []);
  // Client problems from the last blocked submit, in DOM order.
  const [client, setClient] = useState<Problem[]>([]);
  // The server error the person has already resubmitted past (its problems are no longer shown).
  const [stale, setStale] = useState<unknown>(null);
  // Fields edited since `error` arrived: their server message is hidden.
  const [edited, setEdited] = useState<{ error: unknown; ids: string[] }>({ error: null, ids: [] });
  // Bumped on every submit, so the summary is mounted (and announced) again.
  const [attempt, setAttempt] = useState(0);

  const fieldErrors = error instanceof ApiError ? error.fieldErrors : [];
  const fields = Object.entries(registry);
  const server = fieldErrors.map((fe) => {
    const match = fields.find(([, f]) => f.name && matchesName(fe.field, f.name));
    return { id: match?.[0], name: match?.[1].name, message: fe.message };
  });
  const serverMatched = server.some((p) => p.id);
  const showServer = serverMatched && error !== stale;
  const editedIds = edited.error === error ? edited.ids : [];
  const problems: Problem[] = [
    ...client,
    ...(showServer ? server.filter((p) => !(p.id && editedIds.includes(p.id))) : []),
  ];

  // Focus the first field the server rejected, once per error.
  const focused = useRef<unknown>(null);
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors.length || focused.current === error) return;
    const ids = error.fieldErrors
      .map((fe) => Object.entries(registry).find(([, f]) => f.name && matchesName(fe.field, f.name))?.[0])
      .filter((id): id is string => Boolean(id));
    const els = ids.map((id) => document.getElementById(id)).filter((el): el is HTMLElement => el !== null);
    els.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    if (!els[0]) return;
    focused.current = error;
    els[0].focus();
  }, [error, registry]);

  const value: FormContextValue = {
    errorFor: (id, name) => {
      const own = client.find((p) => p.id === id);
      if (own) return own.message;
      if (!showServer || !name || editedIds.includes(id)) return undefined;
      return fieldErrors.find((fe) => matchesName(fe.field, name))?.message;
    },
    register,
    covers: (err) => err === error && serverMatched,
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    const form = e.currentTarget;
    setAttempt((n) => n + 1);
    setStale(error);
    const found: (Problem & { el: Control })[] = [];
    const extra = validate?.(form) ?? {};
    for (const el of Array.from(form.elements)) {
      if (!isControl(el) || !el.willValidate) continue;
      if (el.type === "radio" && found.some((p) => p.el.type === "radio" && p.el.name === el.name)) continue;
      const custom = el.name ? extra[el.name] : undefined;
      // The portal trims what it sends, so spaces alone don't fill a required field.
      const blank = el.required && !NOT_TEXT.has(el.type) && el.value !== "" && el.value.trim() === "";
      if (el.checkValidity() && !custom && !blank) continue;
      if (!el.id) el.id = `form-control-${++generatedIds}`;
      const label = registry[el.id]?.label ?? domLabel(el);
      const message = !el.validity.valid
        ? validityMessage(el, label)
        : blank
          ? `${label} is required`
          : (custom as string);
      found.push({ id: el.id, el, message });
    }
    // Controls outside a Field have no render props, so mark them directly.
    for (const el of Array.from(form.elements))
      if (isControl(el) && !(el.id in registry)) el.removeAttribute("aria-invalid");
    if (found.length) {
      e.preventDefault();
      for (const p of found) if (!(p.el.id in registry)) p.el.setAttribute("aria-invalid", "true");
      setClient(found.map(({ id, message }) => ({ id, message })));
      found[0]?.el.focus();
      return;
    }
    setClient([]);
    onSubmit?.(e);
  };

  const clearFor = (target: EventTarget) => {
    if (!(target instanceof Element) || !isControl(target) || !target.id) return;
    const id = target.id;
    if (client.some((p) => p.id === id)) setClient((prev) => prev.filter((p) => p.id !== id));
    if (!(id in registry)) target.removeAttribute("aria-invalid");
    if (showServer && !editedIds.includes(id) && server.some((p) => p.id === id))
      setEdited({ error, ids: [...editedIds, id] });
  };

  return (
    <FormContext.Provider value={value}>
      <form
        {...rest}
        className={className}
        noValidate
        onSubmit={handleSubmit}
        onInput={(e) => {
          clearFor(e.target);
          onInput?.(e);
        }}
        onChange={(e) => {
          // Selects, checkboxes and custom pickers fire `change` without `input` in some browsers.
          clearFor(e.target);
          rest.onChange?.(e);
        }}
      >
        {problems.length > 0 && (
          <ErrorSummary key={attempt} problems={problems} className={summaryClassName} />
        )}
        {children}
      </form>
    </FormContext.Provider>
  );
}

/** The list of everything wrong, announced when it appears; each item links to (and focuses) its field. */
export function ErrorSummary({ problems, className }: { problems: Problem[]; className?: string }) {
  const headingId = useId();
  const count = problems.length;
  const go = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    focusControl(id);
  };
  return (
    <div
      role="alert"
      aria-labelledby={headingId}
      className={cx(
        "rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-sm text-red-800 dark:text-red-300",
        className,
      )}
    >
      <p id={headingId} className="font-semibold">
        {count === 1 ? "There is a problem" : `There are ${count} problems`}
      </p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5">
        {problems.map((p, i) => (
          <li key={`${p.id ?? "text"}-${i}`}>
            {p.id ? (
              <a href={`#${p.id}`} onClick={(e) => go(e, p.id!)} className="underline hover:no-underline">
                {p.message}
              </a>
            ) : (
              p.message
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface FieldControlProps {
  id: string;
  name?: string;
  required?: boolean;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

/**
 * The state behind `Field` (and any form that lays out its own field): the control id, the message to show (an
 * explicit `error` wins over the Form's), and the props the control needs.
 */
export function useFormField({
  label,
  name,
  error,
  required,
  hasHint,
  hasCount,
  extraDescribedBy,
  id: idProp,
}: {
  label: string;
  name?: string;
  error?: string;
  required?: boolean;
  hasHint?: boolean;
  /** The field shows a `CharacterCount` with id `countId`. */
  hasCount?: boolean;
  extraDescribedBy?: string;
  /** A stable id to use instead of a generated one, e.g. so an `ErrorSummary` elsewhere can link to it. */
  id?: string;
}) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const form = useFormContext();
  const register = form?.register;
  useLayoutEffect(() => register?.(id, { name, label }), [register, id, name, label]);
  const message = error || form?.errorFor(id, name);
  const messageId = message || hasHint ? `${id}-desc` : undefined;
  const countId = `${id}-count`;
  const describedBy =
    [messageId, hasCount && countId, extraDescribedBy].filter(Boolean).join(" ") || undefined;
  const control: FieldControlProps = {
    id,
    ...(name !== undefined && { name }),
    ...(required && { required: true }),
    "aria-invalid": message ? true : undefined,
    "aria-describedby": describedBy,
  };
  return { id, message, messageId, countId, control };
}

/** The visible "required" marker after a label. Screen readers hear the control's own `required`. */
export function RequiredMarker() {
  return (
    <span aria-hidden="true" data-field-marker className="ml-0.5 text-red-600 dark:text-red-400">
      *
    </span>
  );
}

/**
 * A field's `<label>`, with the required marker beside it (outside the label, so the label's text and the
 * control's accessible name stay exactly the field's name).
 */
export function FieldLabel({
  htmlFor,
  required,
  className,
  children,
}: {
  htmlFor: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const label = (
    <label htmlFor={htmlFor} className={className}>
      {children}
    </label>
  );
  if (!required) return label;
  return (
    <div className="flex items-baseline">
      {label}
      <RequiredMarker />
    </div>
  );
}

/** "12 characters left" under a length-limited field. */
export function CharacterCount({ id, length, max }: { id: string; length: number; max: number }) {
  const left = max - length;
  return (
    <p id={id} className={cx("text-xs", left < 0 ? "text-red-600" : "text-fg-subtle")}>
      {left >= 0
        ? `${left} character${left === 1 ? "" : "s"} left`
        : `${-left} character${left === -1 ? "" : "s"} too many`}
    </p>
  );
}
